import React from 'react';
import {createApolloClient} from '@packages/utilities/apolloClient';
import {ApolloClient, ApolloLink} from '@apollo/client';
import {print} from 'graphql';
import {map} from 'rxjs';
import {getPageComponentRender} from '@packages/utilities/platform';
import {isDynamicBoundOrConditioned} from '@packages/utilities/elementTypeUtils';
import {isUserComponentInst} from '@packages/utilities/core-elements';
import {weakMemo} from '@packages/utilities/memo';
import {Component, Expressions} from '@packages/systems/wfdl';
import type {Expression, ExpressionElement} from '@packages/systems/wfdl-ast';
import {getDesignerPreviewOrigin} from '@packages/utilities/canvas-origin';
import {
  hasDynamoUniversalBindings,
  isConnectedDynamoWrapper,
} from '@packages/systems/dynamo/utils/DynamoElementUtils';

const DYNAMO_OPERATION_NAME = 'Dynamo3' as const;

const isDynamicElement = (element: ExpressionElement): boolean =>
  isDynamicBoundOrConditioned(element) ||
  hasDynamoUniversalBindings(element) ||
  isConnectedDynamoWrapper(element);

const expressionKeepsHtmlWhenHidden = (
  keepInHtml: Expression | undefined
): boolean => {
  if (!keepInHtml) return false;
  return Expressions.getUnion(keepInHtml)?.tag === 'True';
};

/**
 * Static counterpart of the renderer's hidden-element check: an element is
 * only treated as hidden here when its visibility conditions are statically
 * false, mirroring `evalVisibilityConditions` (hidden when any condition is
 * not true). Conditions we cannot resolve keep the element visible so the
 * readiness wait stays conservative.
 *
 * Localized visibility is safe under this rule: once a field is localized,
 * its stored expression becomes a localization binding (a Select on
 * `context.localizations.<componentId>.<key>`), never a literal, so a
 * secondary-locale override of a hidden element still counts as "requires
 * data" and the canvas keeps waiting for its queries.
 */
const isStaticallyHidden = (element: ExpressionElement): boolean => {
  const visibility = Expressions.getRecord(
    Expressions.getElementData(element)
  )?.visibility;
  const visibilityFields = visibility
    ? Expressions.getRecord(visibility)
    : null;
  if (!visibilityFields) return false;

  if (expressionKeepsHtmlWhenHidden(visibilityFields.keepInHtml)) return false;

  if (!visibilityFields.conditions) return false;
  const conditions = Expressions.getList(visibilityFields.conditions);
  return Boolean(
    conditions?.some((condition) => {
      const staticBoolean = Expressions.getBoolean(condition);
      if (staticBoolean !== null) return staticBoolean === false;
      return Expressions.getUnion(condition)?.tag === 'False';
    })
  );
};

const or =
  (a: boolean) =>
  (b: boolean): boolean =>
    a || b;

/**
 * Like `expressionContainsSpecificElement` for dynamic elements, but skips
 * subtrees the renderer will not render: elements hidden by statically false
 * visibility conditions are pruned along with their descendants, matching the
 * renderer's hidden-element handling in preview.
 *
 * Like the `Expressions.some`-based walk it replaces, it stops descending
 * under matched dynamic elements and component instances (their slot content
 * is still walked when the component render itself has no match), so it does
 * no more work per node than before.
 */
export const renderedTreeContainsDynamicElement = weakMemo(
  (components: Component.ComponentMap) => {
    const walk = Expressions.everything(or)((expression) => {
      const element = Expressions.getElement(expression);
      if (!element) return Expressions.resume(false);

      if (isStaticallyHidden(element)) {
        return Expressions.stop(false);
      }

      if (isDynamicElement(element)) return Expressions.stop(true);

      if (isUserComponentInst(components, element)) {
        const component = components.get(element.type);
        const componentRoot = component ? Component.getRender(component) : null;
        const foundInComponent = componentRoot ? walk(componentRoot) : false;
        // Keep descending into the instance data so slot content is walked.
        return foundInComponent
          ? Expressions.stop(true)
          : Expressions.resume(false);
      }

      return Expressions.resume(false);
    });

    return (expression: Expression) => walk(expression);
  }
);

export const currentPageRequiresDynamoData = (
  components: Component.ComponentMap
) => {
  const pageRoot = getPageComponentRender(components);

  return pageRoot
    ? renderedTreeContainsDynamicElement(components)(pageRoot)
    : false;
};

/**
 * Hook that:
 * - allows us to detect if there are any Dynamo elements on the page and wait for Dynamo data to be loaded
 * if there are any.
 * - provides reference to ApolloClient
 */
export const useDynamoData = (
  siteId: string | undefined,
  isPageLoading: boolean | undefined,
  components: Component.ComponentMap | undefined
) => {
  const apolloClientRef = React.useRef<ApolloClient | null>(null);
  const [isDynamoDataLoaded, setIsDynamoDataLoaded] = React.useState(false);
  const [areDynamoElementsDetected, setAreDynamoElementsDetected] =
    React.useState<boolean | null>(null);
  const pendingDynamoQueriesRef = React.useRef(new Set<string>());

  if (siteId && !apolloClientRef.current) {
    const detectDynamoDataLoadedLink = new ApolloLink((operation, forward) => {
      if (operation.operationName === DYNAMO_OPERATION_NAME) {
        // Add query to pending set when starting
        pendingDynamoQueriesRef.current.add(print(operation.query));
      }

      return forward(operation).pipe(
        map((result) => {
          if (operation.operationName === DYNAMO_OPERATION_NAME) {
            // Remove query from pending set when completed
            pendingDynamoQueriesRef.current.delete(print(operation.query));
            // Only set isDynamoDataLoaded when all queries are complete
            if (pendingDynamoQueriesRef.current.size === 0) {
              setIsDynamoDataLoaded(true);
            }
          }
          return result;
        })
      );
    });

    apolloClientRef.current = createApolloClient({
      origin: getDesignerPreviewOrigin(window.location.origin),
      path: `/api/v2/sites/${siteId}/apollo`,
      customLinks: [detectDynamoDataLoadedLink],
      // CONT-5981 Phase P: attach the always-on dynamo load counters here too so
      // the preview-workflow canvas frame is ready to emit its own cold-load
      // metric in a follow-up. Inert until a canvas-side load window is opened.
      countDynamoLoadMetrics: true,
      headers: {
        // by adding this header we allow items with derived status of "Draft changes" to be in the results of the query
        'x-wf-designer-client': 'true',
      },
    });
  }

  React.useEffect(() => {
    if (isPageLoading) {
      setIsDynamoDataLoaded(false);
      setAreDynamoElementsDetected(null);
    }
  }, [isPageLoading]);

  React.useEffect(() => {
    if (
      isPageLoading === false &&
      areDynamoElementsDetected === null &&
      components
    ) {
      if (currentPageRequiresDynamoData(components)) {
        setAreDynamoElementsDetected(true);
      } else {
        // if there are no elements bound to Dynamo data on the page we don't need to wait for Dynamo data
        setAreDynamoElementsDetected(false);
        setIsDynamoDataLoaded(true);
      }
    }
  }, [
    isPageLoading,
    isDynamoDataLoaded,
    areDynamoElementsDetected,
    components,
  ]);

  return {apolloClient: apolloClientRef.current, isDynamoDataLoaded};
};
